import { useRef } from 'react'

import { Divider, ActionIcon, Tooltip } from '@mantine/core'
import { Dropzone, IMAGE_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone'
import { Paperclip, Save, Trash2 } from 'lucide-react'

import { useQuery, useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  CREATE_EMAIL_TEMPLATE,
  GET_EMAIL_TEMPLATES,
} from 'src/graphql/emailTemplates.graphql'
import { GET_EMPLOYEE_BY_ID } from 'src/graphql/employees.graphql'
import { cn } from 'src/utils'

import EmailTemplatesMenu from '../../EmailTemplatesMenu/EmailTemplatesMenu'

const EmailModalFooter = ({
  onFileDrop,
  handleClose,
  loading,
  form,
  hasTemplate = false,
  applicantId,
  selectedTemplateState,
  onTemplateStateChange,
  onTemplateSelect,
}) => {
  const uploadRef = useRef<() => void>(null)
  const { currentUser } = useAuth()
  const userId = currentUser?.currentEmployeeId

  const { data } = useQuery(GET_EMPLOYEE_BY_ID, {
    variables: {
      id: userId,
    },
  })

  const {
    data: templatesList,
    loading: templatesLoading,
    error: templatesError,
    refetch: refetchTemplates,
  } = useQuery(GET_EMAIL_TEMPLATES, {
    variables: {
      pageInfo: {
        limit: 100,
        offset: 0,
      },
      orderBy: {
        field: 'title',
        direction: 'asc',
      },
    },
  })

  const [createEmailTemplate, { loading: templateLoading }] = useMutation(
    CREATE_EMAIL_TEMPLATE,
    {
      onCompleted: () => {
        toast('Email template saved successfully!', 'success')
        // Refresh the templates list
        refetchTemplates()
      },
      onError: () => {
        toast('Failed to save email template', 'error')
      },
    }
  )

  const handleSaveAsTemplate = () => {
    const templateData = {
      title: form.values.subject || 'Untitled Template',
      subject: form.values.subject,
      body: form.values.body,
    }

    createEmailTemplate({
      variables: {
        input: templateData,
      },
    })
  }

  const isEmailConnected = data?.employee?.emailConnected

  return (
    <div className="mt-auto flex flex-col justify-center gap-3">
      <Divider />
      <div className="flex flex-row items-center gap-4">
        <Dropzone
          openRef={uploadRef}
          onDrop={(files) => onFileDrop(files)}
          accept={[...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE]}
          maxFiles={10}
          className="hidden"
        />
        <Tooltip
          label={
            hasTemplate
              ? 'File uploads disabled when using template'
              : 'Attach files'
          }
          position="bottom"
          withArrow
          arrowPosition="center"
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => !hasTemplate && uploadRef.current?.()}
            disabled={hasTemplate}
            style={{
              opacity: hasTemplate ? 0.5 : 1,
              cursor: hasTemplate ? 'not-allowed' : 'pointer',
            }}
          >
            <Paperclip size={16} className=" stroke-doubleNickel-gray-500" />
          </ActionIcon>
        </Tooltip>
        <EmailTemplatesMenu
          applicantId={applicantId}
          templatesList={templatesList}
          templatesLoading={templatesLoading}
          templatesError={templatesError}
          selectedTemplate={selectedTemplateState}
          onTemplateChange={onTemplateStateChange}
          onSelect={(template) => {
            form.setFieldValue('subject', template.subject)
            form.setFieldValue('body', template.body)
            onTemplateSelect?.(template)
          }}
          onClear={() => {
            form.setFieldValue('subject', '')
            form.setFieldValue('body', '')
            onTemplateSelect?.(null)
          }}
        />
        <Tooltip
          label={
            !form.isValid()
              ? 'Please enter a subject and body to save as template'
              : 'Save as template'
          }
          position="bottom"
          withArrow
          arrowPosition="center"
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => {
              if (form.isValid()) {
                handleSaveAsTemplate()
              }
            }}
            loading={templateLoading}
            className={cn('ml-auto', !form.isValid() && 'opacity-50')}
          >
            <Save className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
          </ActionIcon>
        </Tooltip>
        <Tooltip
          label="Delete draft"
          position="bottom"
          withArrow
          arrowPosition="center"
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={handleClose}
            disabled={loading}
          >
            <Trash2 size={16} className=" stroke-doubleNickel-gray-500" />
          </ActionIcon>
        </Tooltip>
        <Tooltip
          label="Connect your email in settings"
          position="top"
          withArrow
          arrowPosition="center"
          hidden={isEmailConnected}
        >
          <DNButton
            variant="primary"
            className="min-w-24"
            type="submit"
            loading={loading}
            disabled={!form.isValid() || !isEmailConnected}
          >
            {loading ? 'Sending...' : 'Send Email'}
          </DNButton>
        </Tooltip>
      </div>
    </div>
  )
}

export default EmailModalFooter
